// components/ArticlePage.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, increment, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AnimatedPage } from '../components/AnimatedPage';

const ArticleContainer = styled.div`
  max-width: 740px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const ArticleHeader = styled.header`
  margin-bottom: 40px;
  text-align: center;
`;

const ArticleTitle = styled.h1`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 48px;
  font-weight: 600;
  color: #034C53;
  margin-bottom: 20px;
  line-height: 1.2;
`;

const ArticleMeta = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  color: #6c757d;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
`;

const ArticleContent = styled.div`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 18px;
  line-height: 1.8;
  color: #184A2C;

  h1, h2, h3 {
    color: #034C53;
    margin: 40px 0 20px 0;
  }

  h1 {
    font-size: 32px;
    font-weight: 600;
  }

  h2 {
    font-size: 24px;
    font-weight: 500;
  }

  h3 {
    font-size: 20px;
    font-weight: 500;
  }

  p {
    margin-bottom: 24px;
  }

  blockquote {
    border-left: 4px solid #66BFBF;
    padding-left: 24px;
    margin: 32px 0;
    font-style: italic;
    color: #034C53;
    opacity: 0.8;
  }

  pre {
    background: #f6f8fa;
    border: 1px solid #e1e4e8;
    border-radius: 8px;
    padding: 20px;
    overflow-x: auto;
    margin: 24px 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
    line-height: 1.5;
  }

  code {
    background: #f6f8fa;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
    color: #034C53;
  }

  a {
    color: #66BFBF;
    text-decoration: underline;
    
    &:hover {
      color: #5ab0b0;
    }
  }

  ul, ol {
    margin: 20px 0;
    padding-left: 32px;
  }

  li {
    margin-bottom: 8px;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 24px 0;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #66BFBF;
  text-decoration: none;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 500;
  margin-bottom: 30px;
  padding: 8px 16px;
  border: 1px solid #66BFBF;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #66BFBF;
    color: white;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: 'Clash Grotesk', sans-serif;
  color: #034C53;
  font-size: 18px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: 'Clash Grotesk', sans-serif;
  color: #dc3545;
  
  h2 {
    margin-bottom: 16px;
  }
`;

const EditButton = styled(Link)`
  background: #66BFBF;
  color: white;
  padding: 12px 24px;
  border-radius: 6px;
  text-decoration: none;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #5ab0b0;
    transform: translateY(-1px);
  }
`;

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
useEffect(() => {
  const viewedKey = `viewed_${id}`;
  const now = Date.now();

  // avoid incrementing more than once per 6 hours for same user/device
  const lastViewed = localStorage.getItem(viewedKey);
  if (lastViewed && now - lastViewed <  60 * 60 * 1000) return;

  localStorage.setItem(viewedKey, now);

  const incrementViews = async () => {
    const docRef = doc(db, "articles", id);
    await updateDoc(docRef, { views: increment(1) });
  };

  incrementViews();
}, [id]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const docRef = doc(db, 'articles', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setArticle({
            id: docSnap.id,
            ...docSnap.data()
          });
        } else {
          setError('Article not found');
        }
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchArticle();
    }
  }, [id]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AnimatedPage>
        <LoadingMessage>Loading article...</LoadingMessage>
      </AnimatedPage>
    );
  }

  if (error) {
    return (
      <AnimatedPage>
        <ErrorMessage>
          <h2>Error</h2>
          <p>{error}</p>
          <BackButton to="/blogs">← Back to Articles</BackButton>
        </ErrorMessage>
      </AnimatedPage>
    );
  }

  if (!article) {
    return (
      <AnimatedPage>
        <ErrorMessage>
          <h2>Article Not Found</h2>
          <p>The article you're looking for doesn't exist.</p>
          <BackButton to="/blogs">← Back to Articles</BackButton>
        </ErrorMessage>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
      <ArticleContainer>
        <BackButton to="/blogs">← Back to Articles</BackButton>
        
        <ArticleHeader>
          <ArticleTitle>{article.title}</ArticleTitle>
          <ArticleMeta>
            <span>Published on {formatDate(article.createdAt)}</span>
          
            <span>{article.wordCount} words</span>
            <span>{article.views} view{article?.views !== 1 && `s`}</span>
          </ArticleMeta>
        </ArticleHeader>

        <ArticleContent 
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* <div style={{ marginTop: '40px', textAlign: 'center' }}>
          <EditButton to={`/edit-blog/${article.id}`}>
            Edit Article
          </EditButton>
        </div> */}
      </ArticleContainer>
    </AnimatedPage>
  );
};

export default ArticlePage;